/**
 * FDV — OpenReplay (session replay). Ver docs/analitica-y-cro/13-cro-analytics-tracking.md §5.
 *
 * - Arranca el tracker SOLO cuando `window.FDV.consent.analytics` es true (el bus
 *   `fdv-analytics.js` gestiona el consent; default permisivo = comportamiento Impulse,
 *   se ajusta con la Customer Privacy API cuando el banner esté puesto). Si el consent
 *   se revoca después, se llama `stop()`.
 * - Identidad (ver docs/analitica-y-cro/23-openreplay-checkout-identidad.md §1.3):
 *     · `userID` = `customer.id` de Shopify (si hay login).
 *     · `userAnonymousID` = `FDV.anonId` → OpenReplay cose las sesiones del mismo
 *       visitante antes/después del login, y es la llave de join con el gateway.
 *     · `metadata`: `anon_id`, `template`, y (logueados, no-PII) `orders_count`,
 *       `ltv_bucket` (gasto de VIDA), `account_age_days`. Sin nombre/email/tags.
 * - Enlace con el checkout (que OpenReplay NO graba: otro origen): `wireCheckoutLink()`
 *   pone el atributo oculto `_or_session` en el carrito + `metadata.cart_token`, para
 *   que el pixel / webhook `orders/paid` unan el pedido con esta grabación (Capa 2).
 * - `window.__fdvOpenReplay.event(name, payload)` es el puente de eventos — lo consume
 *   el bus para repartir los `fdv_*` (`fdv_begin_checkout` incluido). Buffer hasta que
 *   el tracker arranca. `.sessionID()` devuelve el id de sesión (o null).
 * - Los hooks legacy de Impulse (`ajaxProduct:added`, monkey-patch de `add_to_cart_grid`)
 *   NO se portan: el bus es la única fuente de eventos.
 *
 * Tracker: `assets/openreplay-tracker.js` (snapshot pineado de la v18.1.5). El stub de
 * abajo es el patrón oficial de OpenReplay (cola de llamadas + carga async del tracker);
 * solo se cambió el `src` para servirlo desde el CDN de Shopify. Tras cargar, el tracker
 * reemplaza `window.OpenReplay` por su instancia real y reproduce la cola en orden
 * (`[0, startOpts]` → `start`, luego los `event` bufferizados).
 */
(() => {
  const cfg = readConfig();
  if (!cfg.projectKey || !cfg.trackerUrl) return;

  // --- Gate de prod: graba solo cuando (a) se está sirviendo el tema PUBLICADO
  //     (`Shopify.theme.role === 'main'` — falso en `theme dev`, tema dev pusheado,
  //     preview de un tema sin publicar y `*.shopifypreview.com`; el editor ni llega
  //     acá, el snippet no se renderiza en `request.design_mode`) Y (b) el navegador
  //     está en el dominio canónico (`location.hostname` == host de `cfg.primaryUrl`
  //     = `shop.url`, con allowlist hardcodeada de respaldo; nunca `*.myshopify.com`).
  //     NO se usa `request.host` de Liquid: el check server-side viejo
  //     (`request.host == shop.primary_domain.host`) daba falso negativo permanente en
  //     el live porque `shop.primary_domain.host` devuelve `null` en este tema.
  //     Escotilla de prueba fuera de prod: localStorage['fdv_or_force'] = '1'. Sin
  //     arrancar → el bus (`window.__fdvOpenReplay`) hace no-op limpio. ----------------
  const PROD_HOSTS = ['flordevenus.com.mx'];
  const stripWww = (h) => (h || '').replace(/^www\./, '');
  let primaryHost = '';
  try {
    if (cfg.primaryUrl) primaryHost = stripWww(new URL(cfg.primaryUrl).hostname);
  } catch (e) {
    /* noop */
  }
  const here = stripWww(location.hostname);
  const themeRole = (window.Shopify && window.Shopify.theme && window.Shopify.theme.role) || '';
  const hostOk =
    here !== '' &&
    !/\.(myshopify\.com|shopifypreview\.com)$/.test(here) &&
    ((primaryHost !== '' && here === primaryHost) || PROD_HOSTS.indexOf(here) !== -1);
  const onProd = themeRole === 'main' && hostOk;
  if (!onProd && safeLS('fdv_or_force') !== '1') return;

  // --- Puente de eventos: disponible YA, con buffer hasta que arranque el tracker ------
  const buffer = [];
  let started = false;

  window.__fdvOpenReplay = {
    event(name, payload) {
      if (typeof name !== 'string') return;
      if (started && canCall('event')) {
        try {
          window.OpenReplay.event(name, payload);
        } catch (e) {
          /* noop */
        }
      } else {
        buffer.push([name, payload]);
      }
    },
    // Id de la sesión de replay, o null hasta que responde el handshake `/start`.
    sessionID() {
      try {
        return (started && canCall('getSessionID') && window.OpenReplay.getSessionID()) || null;
      } catch (e) {
        return null;
      }
    },
  };

  // --- Gate de consent -----------------------------------------------------------------
  withFDV((FDV) => {
    if (FDV.consent?.analytics) start(FDV);
    document.addEventListener('fdv:consent', (e) => {
      const ok = e.detail?.analytics;
      if (ok) start(FDV);
      else if (started && canCall('stop')) {
        try {
          window.OpenReplay.stop();
        } catch (err) {
          /* noop */
        }
      }
    });
  });

  function start(FDV) {
    if (started) return;
    started = true;

    const ignore = Array.isArray(cfg.ignoreNetwork) ? cfg.ignoreNetwork : [];
    const initOpts = {
      projectKey: cfg.projectKey,
      ingestPoint: cfg.ingestPoint || undefined,
      defaultInputMode: 2, // 2 = obscure all inputs (mismo que Impulse)
      obscureTextEmails: true,
      obscureTextNumbers: false,
      // Fuera del dominio de prod (escotilla `localStorage.fdv_or_force`): OpenReplay
      // bloquea localhost / sin-SSL por "secure mode". En prod real NO se pasa el flag.
      __DISABLE_SECURE_MODE: !onProd,
      network: {
        capturePayload: false,
        sanitizer: (data) => {
          const url = (data && data.url) || '';
          if (ignore.some((frag) => frag && url.indexOf(frag) !== -1)) return null;
          return data;
        },
      },
    };

    const startOpts = { metadata: { anon_id: FDV.anonId || '' } };
    if (cfg.template) startOpts.metadata.template = cfg.template;
    if (cfg.customer_id != null && cfg.customer_id !== '') {
      startOpts.userID = String(cfg.customer_id);
    } else if (FDV.claimedCustomerId) {
      // Sin login: id de cliente RECLAMADO desde un enlace de correo (`?c_id=`).
      // Metadata para poder filtrar replays de "clientes que llegan de campaña",
      // NO `userID` (es una pista sin verificar — ver fdv-analytics.js). La clave
      // debe declararse en el proyecto OpenReplay (flor_venus_openreplay).
      startOpts.metadata.claimed_customer_id = String(FDV.claimedCustomerId);
    }

    // Metadata no-PII del cliente logueado. Sin nombre / email / tags — ver
    // docs/analitica-y-cro/23-openreplay-checkout-identidad.md §1.3.
    const c = cfg.customer;
    if (c && typeof c === 'object') {
      if (c.orders_count != null) startOpts.metadata.orders_count = String(c.orders_count);
      if (c.total_spent_cents != null) {
        startOpts.metadata.ltv_bucket = ltvBucket(Number(c.total_spent_cents) / 100);
      }
      if (c.created_at) {
        const days = Math.floor((Date.now() / 1000 - parseInt(c.created_at, 10)) / 86400);
        if (days >= 0) startOpts.metadata.account_age_days = String(days);
      }
    }

    loadTracker(cfg.trackerUrl, initOpts, startOpts);

    // `anon_id` como identidad anónima real (no solo filtro de metadata) → cose las
    // sesiones pre/post login del mismo visitante. Encolado en el stub, el tracker
    // real lo reproduce tras `start`.
    if (FDV.anonId) {
      try {
        window.OpenReplay.setUserAnonymousID(String(FDV.anonId));
      } catch (e) {
        /* noop */
      }
    }

    flushBuffer();
    wireCheckoutLink();
  }

  function canCall(method) {
    return window.OpenReplay && typeof window.OpenReplay[method] === 'function';
  }

  function flushBuffer() {
    while (buffer.length) {
      const [name, payload] = buffer.shift();
      if (!canCall('event')) break;
      try {
        window.OpenReplay.event(name, payload);
      } catch (e) {
        /* noop */
      }
    }
  }

  /**
   * Cohorte del gasto de VIDA del cliente (MXN). Cortes = fuente de verdad
   * `flor_venus_openreplay/docs/metadata-keys.md` (un analista filtra por estos
   * strings en el dashboard). Ver doc 23 §1.3.
   */
  function ltvBucket(mxn) {
    if (!(mxn > 0)) return '0';
    if (mxn < 1000) return '1-999';
    if (mxn < 3000) return '1000-2999';
    if (mxn < 5000) return '3000-4999';
    if (mxn < 10000) return '5000-9999';
    if (mxn < 20000) return '10000-19999';
    return '20000+';
  }

  /**
   * Enlaza esta grabación con el checkout, que OpenReplay NO graba (otro origen):
   *   - `setMetadata('cart_token', …)` → filtrar replays por carrito en el dashboard.
   *   - atributo OCULTO `_or_session` en el carrito → viaja a `checkout.attributes`
   *     (pixel) y `note_attributes` (webhook `orders/paid`); el gateway lo usa para
   *     unir el pedido con esta sesión (Capa 2, docs/23-*.md).
   * Proactivo, NO en el click de "Finalizar compra": el `<form>` del carrito navega
   *   antes de que un `/cart/update.js` alcance a llegar. El `sessionID` no existe
   *   hasta que responde el handshake `/start` → se reintenta; y se re-sella tras
   *   cada cambio de carrito (`fdv:gifts:state`, que emite el gift engine).
   */
  function wireCheckoutLink() {
    let stampedSid = null;
    let metaDone = false;

    const run = async () => {
      const sid = (window.__fdvOpenReplay && window.__fdvOpenReplay.sessionID()) || null;
      if (!sid) return;

      if (!metaDone && canCall('setMetadata')) {
        const tok = await cartToken();
        if (tok) {
          try {
            window.OpenReplay.setMetadata('cart_token', String(tok));
            metaDone = true;
          } catch (e) {
            /* noop */
          }
        }
      }

      if (stampedSid !== sid && cartHasItems()) {
        stampedSid = sid;
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { _or_session: String(sid) } }),
            keepalive: true,
          });
        } catch (e) {
          stampedSid = null;
        }
      }
    };

    let tries = 0;
    const iv = setInterval(() => {
      run().finally(() => {
        if (stampedSid || ++tries > 40) clearInterval(iv);
      });
    }, 500);

    document.addEventListener('fdv:gifts:state', () => {
      run();
    });
  }

  function cartHasItems() {
    const cart = window.FDV && window.FDV.gifts && window.FDV.gifts.cart;
    return !!(cart && cart.item_count > 0);
  }

  async function cartToken() {
    try {
      const cart = window.FDV && window.FDV.gifts && window.FDV.gifts.cart;
      if (cart && cart.token) return cart.token;
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      const j = await res.json();
      return j && j.token ? j.token : null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Stub oficial de OpenReplay: crea `window.OpenReplay` como cola, encola el `start` con
   * `startOpts` (`[s-1, e]` = `[0, startOpts]`), carga el tracker async y le pasa la cola.
   */
  function loadTracker(src, initOpts, startOpts) {
    (function (A, s, a, y, e, r) {
      r = window.OpenReplay = [e, r, y, [s - 1, e]];
      s = document.createElement('script');
      s.src = A;
      s.async = !a;
      document.getElementsByTagName('head')[0].appendChild(s);
      r.start = function () {
        return r.push([0]);
      };
      r.stop = function () {
        return r.push([1]);
      };
      r.setUserID = function (id) {
        return r.push([2, id]);
      };
      r.setUserAnonymousID = function (id) {
        return r.push([3, id]);
      };
      r.setMetadata = function (k, v) {
        return r.push([4, k, v]);
      };
      r.event = function (k, p, i) {
        return r.push([5, k, p, i]);
      };
      r.issue = function (k, p) {
        return r.push([6, k, p]);
      };
      r.isActive = function () {
        return false;
      };
      r.getSessionToken = function () {};
    })(src, 1, 0, initOpts, startOpts);
  }

  // =====================================================================================
  function readConfig() {
    try {
      return JSON.parse(document.getElementById('fdv-openreplay-config')?.textContent || '{}');
    } catch (e) {
      return {};
    }
  }

  function safeLS(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function withFDV(cb, tries = 40) {
    if (window.FDV) return cb(window.FDV);
    if (tries <= 0) return;
    setTimeout(() => withFDV(cb, tries - 1), 50);
  }
})();
